import './style.scss';

import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { Card, Icon, Modal, ModalProps } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { ThumbnailImageRenderer } from 'app/components/ThumbnailImageRenderer';
import { DisplayableImageData } from 'app/interfaces/Image';
import { BasicDisplayOptions } from 'app/interfaces/ImageRenderer';

export type PreviewableImage = {
  id: string | number;
  image?: DisplayableImageData;
} & BasicDisplayOptions;

export type ImagePreviewContextType = {
  previewImageIndex: number | null;
  setPreviewImageIndex: (index: number | null) => void;
  openPreview: (index: number) => void;
  closePreview: () => void;
};

const ImagePreviewContext = createContext<ImagePreviewContextType | null>(null);

export const useImagePreview = () => {
  const context = useContext(ImagePreviewContext);
  if (!context) {
    throw new Error('useImagePreview must be used within ModalWithSideImagePreview');
  }
  return context;
};

export type ModalWithSideImagePreviewProps = Omit<ModalProps, 'children' | 'content'> & {
  previewableImages: PreviewableImage[];
  mainCardClassName?: string;
  previewCardClassName?: string;
  previewTitle?: ReactNode;
  contentClassName?: string;
};

export function ModalWithSideImagePreview({
  children,
  previewableImages,
  mainCardClassName = '',
  previewCardClassName = '',
  previewTitle,
  contentClassName = '',
  open,
  onClose,
  onOpen,
  ...modalProps
}: ModalWithSideImagePreviewProps) {
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null);

  const openPreview = useCallback((index: number) => {
    setPreviewImageIndex(index);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewImageIndex(null);
  }, []);

  const handleClose = useCallback(
    (event: React.MouseEvent<HTMLElement>, data: ModalProps) => {
      setPreviewImageIndex(null);
      onClose?.(event, data);
    },
    [onClose]
  );

  const contextValue: ImagePreviewContextType = useMemo(
    () => ({
      previewImageIndex,
      setPreviewImageIndex,
      openPreview,
      closePreview,
    }),
    [previewImageIndex, setPreviewImageIndex, openPreview, closePreview]
  );

  const hasPreview = previewImageIndex !== null;

  return (
    <ImagePreviewContext.Provider value={contextValue}>
      <Modal
        open={open}
        onClose={handleClose}
        onOpen={onOpen}
        basic
        {...modalProps}
        size={hasPreview ? 'fullscreen' : modalProps.size}
      >
        <Card.Group centered itemsPerRow={2}>
          <Card className={`modal-with-preview modal-with-preview__main ${mainCardClassName}`}>
            {children}
          </Card>
          {hasPreview && (
            <Card
              className={`modal-with-preview modal-with-preview__preview ${previewCardClassName}`}
            >
              <Card.Content className="modal-with-preview__header imagePreview__header">
                <span>
                  {previewTitle ?? <FormattedMessage id="exportStudyModal.imagePreview.title" />}
                </span>
                <Icon
                  name="close"
                  className="imagePreview__close"
                  onClick={closePreview}
                  data-testid="close-preview"
                />
              </Card.Content>
              <Card.Content className="imagePreview__container">
                <div className="imagePreview__renderer">
                  <ThumbnailImageRenderer
                    image={previewableImages[previewImageIndex]?.image}
                    viewport={previewableImages[previewImageIndex]?.viewport}
                    annotations={previewableImages[previewImageIndex]?.annotations}
                    metadata={previewableImages[previewImageIndex]?.metadata}
                    displayWithAnnotations
                  />
                </div>
              </Card.Content>
            </Card>
          )}
        </Card.Group>
      </Modal>
    </ImagePreviewContext.Provider>
  );
}

// Add subcomponents
ModalWithSideImagePreview.Header = ({
  children,
  className = '',
  ...props
}: {
  children: ReactNode;
  className?: string;
}) => (
  <Card.Content className={`modal-with-preview__header header ${className}`} {...props}>
    {children}
  </Card.Content>
);

ModalWithSideImagePreview.Content = ({
  children,
  className = '',
  ...props
}: {
  children: ReactNode;
  className?: string;
}) => (
  <Card.Content className={`modal-with-preview__content ${className}`} {...props}>
    {children}
  </Card.Content>
);

ModalWithSideImagePreview.Actions = ({
  children,
  className = '',
  ...props
}: {
  children: ReactNode;
  className?: string;
}) => (
  <Card.Content className={`modal-with-preview__actions ${className}`} {...props}>
    {children}
  </Card.Content>
);

export default ModalWithSideImagePreview;
